// src/pages/purchase-requisition/hooks/useUserTooltipData.jsx
import { useState, useCallback, useMemo } from "react";
import { get, ref } from "firebase/database";
import { database } from "../../../firebase";

// Type definitions untuk TypeScript (opsional)
/**
 * @typedef {Object} TooltipContent
 * @property {string} name
 * @property {string} department
 * @property {string} jabatan
 * @property {string[]} roles
 */

/**
 * @typedef {Object} TooltipState
 * @property {boolean} open
 * @property {TooltipContent} content
 * @property {HTMLElement | null} anchorEl
 */

const defaultTooltipContent = {
  name: '',
  department: '',
  jabatan: '',
  roles: []
};

const useUserTooltipData = () => {
  const [state, setState] = useState({
    open: false,
    content: defaultTooltipContent,
    anchorEl: null
  });

  /**
   * Fetch role names secara parallel
   * @param {string[]} roleIds 
   * @returns {Promise<string[]>}
   */
  const fetchRoleNames = useCallback(async (roleIds) => {
    if (!roleIds || roleIds.length === 0) return [];
    
    try {
      const rolePromises = roleIds.map(async (roleId) => {
        const roleSnap = await get(ref(database, `db_config/db_wfms/tb_role/${roleId.trim()}`));
        return roleSnap.exists() ? roleSnap.val().nama_role : null;
      });

      const roles = await Promise.all(rolePromises);
      return roles.filter(Boolean);
    } catch (error) {
      console.error("Error fetching roles:", error);
      return [];
    }
  }, []);

  /**
   * Mengambil detail pengguna untuk tooltip
   * @param {string} uid 
   * @returns {Promise<TooltipContent>}
   */
  const fetchUserDetails = useCallback(async (uid) => {
    if (!uid) return defaultTooltipContent;

    try {
      const userSnap = await get(ref(database, `db_user/tb_user/${uid}`));
      const userData = userSnap.val();

      if (!userData) {
        return {
          ...defaultTooltipContent,
          name: 'Pengguna tidak ditemukan'
        };
      }

      // Fetch parallel untuk optimasi performa
      const [departmentName, jabatanName, roles] = await Promise.all([
        get(ref(database, `db_config/db_wfms/tb_department/${userData.id_department}`))
          .then(snap => snap.exists() ? snap.val().nama_department : 'N/A'),
        get(ref(database, `db_config/db_wfms/tb_jabatan/${userData.id_jabatan}`))
          .then(snap => snap.exists() ? snap.val().nama_jabatan : 'N/A'),
        userData.id_role 
          ? fetchRoleNames(userData.id_role.split(';'))
          : Promise.resolve(['N/A'])
      ]);

      return {
        name: userData.name || 'N/A',
        department: departmentName,
        jabatan: jabatanName,
        roles: roles.length > 0 ? roles : ['N/A']
      };
    } catch (error) {
      console.error("Error fetching user details:", error);
      return {
        ...defaultTooltipContent,
        name: 'Error fetching data'
      };
    }
  }, [fetchRoleNames]);

  /**
   * Handler untuk membuka tooltip
   * @param {React.MouseEvent} event 
   * @param {string} uid 
   */
  const handleTooltipOpen = useCallback(async (event, uid) => {
    if (!uid) return;

    setState(prev => ({
      ...prev,
      open: true,
      anchorEl: event.currentTarget,
      content: { ...prev.content, name: 'Loading...' } // Feedback loading
    }));

    const content = await fetchUserDetails(uid);
    
    setState(prev => ({
      ...prev,
      content
    }));
  }, [fetchUserDetails]);

  /**
   * Handler untuk menutup tooltip
   */
  const handleTooltipClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      open: false,
      anchorEl: null,
      content: defaultTooltipContent
    }));
  }, []);

  // Menggunakan useMemo untuk optimasi performa render
  const tooltipContext = useMemo(() => ({
    tooltipOpen: state.open,
    tooltipContent: state.content,
    tooltipAnchorEl: state.anchorEl,
    handleTooltipOpen,
    handleTooltipClose
  }), [state.open, state.content, state.anchorEl, handleTooltipOpen, handleTooltipClose]);

  return tooltipContext;
};

export default useUserTooltipData;